// @flow

import React from 'react';
import styled from '@emotion/styled';

import t from 'app/utils/translate';
import type { TabItem } from 'app/types';
import ChallengeInfoContent from './ChallengeInfoContent';
import ChallengeInfoTab from './ChallengeInfoTab';
import LightTabSet from '../commons/LightTabSet';
import MenuWrapper from '../commons/MenuWrapper';

type Props = {
  activeTabItem: ?TabItem,
  bonuses: Array<*>,
  challengeId: string,
  className?: string,
  countries: Array<*>,
  disableTabs?: Array<string>,
  entryCap: number,
  entryCount: number,
  games: Array<*>,
  isRacing: boolean,
  isUnlimited: boolean,
  labels?: Array<*>,
  maxEntriesPerPerson: number,
  onChange: TabItem => void,
  onClose?: () => void,
  payoutMessage?: string,
  prizes?: Array<*>,
  scheme?: 'light' | 'dark' | 'white',
  scoring?: Array<*>,
  statusId: number
};

const defaultProps = {
  className: undefined,
  disableTabs: [],
  labels: [],
  onClose: undefined,
  payoutMessage: undefined,
  prizes: [],
  scheme: 'light',
  scoring: []
};

const StyledDiv = styled.div(
  ({ theme }) => ({
    [theme.mediaQuery.desktopAndUp]: {
      fontSize: theme.fontSize.xsmall
    }
  }),
  ({ theme, scheme }) =>
    ({
      white: {
        backgroundColor: theme.color.white,
        color: theme.color.basis1
      },
      light: {
        color: theme.color.basis1
      },
      dark: {
        color: theme.color.white
      }
    }[scheme])
);

const tabNames = ['prizes', 'scoring', 'games', 'entries', 'labels'];

const NavWrapper = styled.div(
  {
    display: 'flex',
    position: 'relative'
  },
  ({ theme }) => ({
    backgroundColor: theme.color.basis6,
    justifyContent: 'center'
  })
);

const TabContainer = styled.div({
  width: '100%',
  maxWidth: 120 * tabNames.length
});

function ChallengeInfo({
  activeTabItem,
  bonuses,
  challengeId,
  className,
  countries,
  disableTabs,
  entryCap,
  entryCount,
  games,
  isRacing,
  isUnlimited,
  labels,
  maxEntriesPerPerson,
  onChange,
  onClose,
  payoutMessage,
  prizes,
  scheme,
  scoring,
  statusId
}: Props) {
  return (
    <StyledDiv className={className} scheme={scheme}>
      <NavWrapper>
        <TabContainer>
          <MenuWrapper component={LightTabSet} activeProp="active" value={activeTabItem}>
            <ChallengeInfoTab id="prizes" onSelect={onChange}>
              {t('Prizes')}
            </ChallengeInfoTab>
            <ChallengeInfoTab id="scoring" onSelect={onChange}>
              {t('Scoring')}
            </ChallengeInfoTab>
            <ChallengeInfoTab id="games" onSelect={onChange}>
              {t(isRacing ? 'Legs' : 'Games')}
            </ChallengeInfoTab>
            {disableTabs.includes('entries') === false && (
              <ChallengeInfoTab id="entries" onSelect={onChange}>
                {t('Entries')}
              </ChallengeInfoTab>
            )}
            <ChallengeInfoTab id="labels" onSelect={onChange}>
              {t('Labels')}
            </ChallengeInfoTab>
          </MenuWrapper>
        </TabContainer>
      </NavWrapper>
      {!!activeTabItem && (
        <ChallengeInfoContent
          activeTabItem={activeTabItem}
          bonuses={bonuses}
          challengeId={challengeId}
          countries={countries}
          entryCap={entryCap}
          entryCount={entryCount}
          games={games}
          isRacing={isRacing}
          isUnlimited={isUnlimited}
          labels={labels}
          maxEntriesPerPerson={maxEntriesPerPerson}
          onClose={onClose}
          payoutMessage={payoutMessage}
          prizes={prizes}
          scheme={scheme}
          scoring={scoring}
          statusId={statusId}
        />
      )}
    </StyledDiv>
  );
}

ChallengeInfo.defaultProps = defaultProps;

export default ChallengeInfo;
